import React from 'react';
import { CheckCircle, Star, Zap } from 'lucide-react';

const PricingTiers = ({ onSelectPlan }) => {
  const plans = [
    {
      name: "Budget Plan",
      badge: "Most Popular",
      badgeColor: "bg-blue-500",
      downPayment: "From $50",
      description: "Perfect for families on a tight budget",
      features: [
        "As low as $50 down payment",
        "Extended payment terms up to 12 months",
        "No credit check required",
        "Same-day approval",
        "24/7 agent support"
      ],
      cta: "Choose Budget Plan",
      ctaColor: "bg-blue-600 hover:bg-blue-700"
    },
    {
      name: "0% Down Plan",
      badge: "Best Value",
      badgeColor: "bg-green-500",
      downPayment: "$0 Down",
      description: "Subject to approved credit (OAC)",
      features: [
        "No money down required (OAC)",
        "Quick credit approval process",
        "Standard 6-month payment terms",
        "Competitive monthly rates",
        "Priority processing"
      ],
      cta: "Apply for 0% Down",
      ctaColor: "bg-green-600 hover:bg-green-700"
    },
    {
      name: "Express Plan",
      badge: "Fastest",
      badgeColor: "bg-purple-500",
      downPayment: "25% Down",
      description: "For immediate release with lower monthly payments",
      features: [
        "25% down for lower monthly payments",
        "Shortest payment terms (3-6 months)",
        "Instant approval process",
        "Priority jail processing",
        "Dedicated agent assistance"
      ],
      cta: "Choose Express Plan",
      ctaColor: "bg-purple-600 hover:bg-purple-700"
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-6">
            Choose Your Payment Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We have flexible options to fit every budget. All plans include the same professional service and 24/7 support.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {plans?.map((plan, index) => (
            <div key={index} className="relative bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              {/* Badge */}
              {plan?.badge && (
                <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 ${plan?.badgeColor} text-white px-4 py-1 rounded-full text-sm font-bold`}>
                  {plan?.badge}
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-black text-gray-900 mb-2">{plan?.name}</h3>
                <div className="text-4xl font-black text-blue-600 mb-2">{plan?.downPayment}</div>
                <p className="text-gray-600">{plan?.description}</p>
              </div>

              <div className="space-y-4 mb-8">
                {plan?.features?.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  onSelectPlan?.(plan);
                  window.location.href = 'tel:9162563468';
                }}
                className={`w-full ${plan?.ctaColor} text-white px-6 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg`}
              >
                {plan?.cta}
              </button>

              <p className="text-center text-sm text-gray-500 mt-3">
                Call (916) 256-3468 to apply
              </p>
            </div>
          ))}
        </div>

        {/* Additional Benefits */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            All Plans Include These Benefits
          </h3>
          
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Fast Processing</h4>
              <p className="text-gray-600 text-sm">2-4 hour average release time</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Licensed Agents</h4>
              <p className="text-gray-600 text-sm">Professional, bonded service</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Star className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">24/7 Support</h4>
              <p className="text-gray-600 text-sm">Always available when you need us</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Secure Process</h4>
              <p className="text-gray-600 text-sm">Protected information & payments</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingTiers;